import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import PageHelmet from "../Helper/PageHelmet";
import { Link } from "react-router-dom";
import {
  LuFileText,
  LuAlertCircle,
  LuSplitSquareHorizontal,
} from "react-icons/lu";
import MidstreamContactForm from "./MidstreamContactForm";
import MidstreamFooter from "./MidstreamFooter";
import energyHero from "../../assets/midstream-product-images/energy_hero.png";
import inletPriority from "../../assets/midstream-product-images/energy_inlet_priority.png";
import energyInvestment from "../../assets/midstream-product-images/energy_investment.png";
import energyDesignChoice from "../../assets/midstream-product-images/energy_design_choice.png";
import energyReprice from "../../assets/midstream-product-images/energy_reprice.png";
import soc2Seal from "../../assets/midstream-product-images/soc2-seal.png";
import logoMark from "../../assets/logos/header_logo.png";

const navLinks = [
  { label: "The Problem", href: "#problem" },
  { label: "What we do", href: "#what-we-do" },
  { label: "Where the value is", href: "#money" },
  { label: "How we started", href: "#how" },
];

const footerLinks = [
  { label: "The problem", href: "#problem" },
  { label: "What we do", href: "#what-we-do" },
  { label: "Where the value is", href: "#money" },
  { label: "How we start", href: "#how" },
  { label: "Find out in two weeks", href: "#contact" },
];

const heroStats = [
  { label: "Operational analysis of running clean-tech plants, not only project economics" },
  { label: "Projects modeled across hydrogen, capture, biofuels, and geothermal" },
  { label: "Start with the data you have today. No integration required." },
];

const problems = [
  {
    icon: LuFileText,
    title: "One workbook, one author.",
    text: "When they leave, so does the logic. When inputs change, someone rebuilds it by hand.",
  },
  {
    icon: LuAlertCircle,
    title: "Technology & finance in different rooms.",
    text: "The process engineer's mass and energy balance and the lender's return model rarely agree on the same page.",
  },
  {
    icon: LuSplitSquareHorizontal,
    title: "Everything keeps moving.",
    text: "Power prices, feedstock costs, carbon intensity scores, and incentive rules shift quarter to quarter, and each shift triggers a rebuild.",
  },
];

const pillars = [
  {
    tag: "Engineering rigor",
    title: "Physics behind every number.",
    text: "Your engineers can inspect every assumption. Your investors know the technology has been modeled, not assumed.",
  },
  {
    tag: "Investor-defensible",
    title: "An audit trail for every decision.",
    text: "Scenario, assumptions, outcome, and the math behind each, one click away, ready for diligence.",
  },
  {
    tag: "Agents",
    title: "Scenarios that re-run themselves.",
    text: "When a price, an input, or a policy changes, the case is re-priced in minutes instead of rebuilt by hand.",
  },
];

const flow = [
  { label: "Wellhead", tone: "mint" },
  { label: "Gathering", tone: "mint" },
  { label: "Plant", tone: "mint" },
  { label: "Pipeline", tone: "amber" },
  { label: "Settlement", tone: "amber" },
];

const moneyPlaces = [
  {
    tag: "Integrated economics",
    title: "The investment decision.",
    image: energyInvestment,
    text: "Capex, operating cost, carbon intensity, incentives, and offtake in one model, with the sensitivities that decide whether the project clears the bar. Know what has to be true for it to pay.",
  },
  {
    tag: "Compare Trade-offs",
    title: "The design choice.",
    image: energyDesignChoice,
    text: "Compare pathways, configurations, and scale side by side, with the physics checked, before engineering spend is committed. For capture projects, that includes capture rate versus cost.",
  },
  {
    tag: "Valuation before commitment.",
    title: "The incentive and offtake structure.",
    image: energyInvestment,
    text: "Price, tenor, tax credit qualification, and carbon intensity score, converted into value per unit. See what a contract term is worth before you sign it.",
  },
  {
    tag: "Real-time re-pricing",
    title: "When the inputs move.",
    image: energyReprice,
    text: "Power, feedstock, or policy changes. Dsider re-prices the case in minutes and shows which assumption moved the answer.",
  },
];

const alsoInModel = [
  "Hydrogen and ammonia.",
  "Point source capture and direct air capture.",
  "Biofuels and ethanol.",
  "Lifecycle carbon accounting.",
];

const steps = [
  {
    when: "Weeks 1–2",
    title: "Discovery.",
    text: "Send us the production data, well files, and facility exports you already have. Together we pick the one decision worth solving first. A short, paid engagement.",
  },
  {
    when: "Days 15–90",
    title: "Prove the number.",
    text: "We build the model on your data. Your lead reservoir or production engineer reviews it. At day 90 you have a directional value number your CFO can check.",
  },
  {
    when: "Then",
    title: "Decide with your eyes open.",
    text: "If the number isn't there, you've spent a few hours over the quarter reviewing our work. If it is, you sign knowing exactly what you're buying.",
  },
];

const audiences = [
  {
    tag: "Your engineers.",
    title: "A first-principles simulation with every assumption visible and editable. Put your lead process engineer in the first review and verify the physics.",
    visual: "priority",
  },
  {
    tag: "Your investors, lender, and partners.",
    title: "An AI story with a dollar sign: models grounded in physics that show exactly how the return was built, case by case.",
    visual: "seal",
  },
];

const HOVER_DELAY = 250;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el)
    el.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "start",
    });
};

// Stagger index for entrance animations (read as var(--d) in the SCSS)
const d = (i) => ({ "--d": i });

const EnergyTransition = () => {
  const [activeId, setActiveId] = useState("");
  const hoverTimer = useRef(null);
  const rootRef = useRef(null);
  const moneyRef = useRef(null);
  const pinRef = useRef(null);
  const trackRef = useRef(null);
  const dotsRef = useRef(null);
  const hintRef = useRef(null);

  // Reveal .ms-reveal elements once as they scroll into view
  useEffect(() => {
    const items = rootRef.current.querySelectorAll(".ms-reveal");
    if (!("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Highlight the nav link of the section currently in view.
  // Nothing is highlighted while the hero (above the first section) is in view.
  useEffect(() => {
    const sections = navLinks
      .map((l) => document.getElementById(l.href.slice(1)))
      .filter(Boolean);
    let frame = null;

    const updateActive = () => {
      frame = null;
      const line = window.innerHeight * 0.4;
      let current = "";
      sections.forEach((el) => {
        if (el.getBoundingClientRect().top <= line) current = el.id;
      });
      setActiveId(current);
    };

    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(updateActive);
    };

    updateActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
      clearTimeout(hoverTimer.current);
    };
  }, []);

  // "Where the money is": on tablet/desktop the section pins below the nav and
  // vertical scroll moves the card track sideways (1:1, lightly smoothed).
  // It unpins once the last card is in place. Tab / arrow keys move card by card.
  useEffect(() => {
    const section = moneyRef.current;
    const pin = pinRef.current;
    const sticky = pin.firstElementChild;
    const track = trackRef.current;
    const dots = Array.from(dotsRef.current.children);
    const hint = hintRef.current;
    const cards = Array.from(track.children);
    const wide = window.matchMedia("(min-width: 768px)");
    let enabled = false;
    let distance = 0;
    let pinTop = 0;
    let current = 0;
    let target = 0;
    let frame = null;
    let active = -1;

    const render = () => {
      const progress = distance ? current / distance : 0;
      track.style.transform = `translate3d(${-current}px, 0, 0)`;
      // highlight the card (and dot) matching the scroll position
      const next = Math.round(progress * (cards.length - 1));
      if (next !== active) {
        active = next;
        cards.forEach((c, i) => c.classList.toggle("is-active", i === active));
        dots.forEach((dot, i) => dot.classList.toggle("is-active", i === active));
      }
      hint.classList.toggle("is-done", progress > 0.98);
    };

    // ease the track toward the scroll position
    const tick = () => {
      frame = null;
      current += (target - current) * 0.2;
      if (Math.abs(target - current) < 0.5) current = target;
      render();
      if (current !== target) frame = requestAnimationFrame(tick);
    };

    const readTarget = () => {
      const scrolled = pinTop - pin.getBoundingClientRect().top;
      target = Math.min(distance, Math.max(0, scrolled));
    };

    const onScroll = () => {
      if (!enabled) return;
      readTarget();
      if (frame === null) frame = requestAnimationFrame(tick);
    };

    const measure = () => {
      enabled = wide.matches && !prefersReducedMotion();
      section.classList.toggle("is-hscroll", enabled);
      track.style.transform = "";
      pin.style.height = "";
      active = -1;
      distance = 0;
      current = 0;
      target = 0;
      if (!enabled) return;
      pinTop = parseFloat(getComputedStyle(sticky).top) || 0;
      distance = Math.max(0, track.scrollWidth - track.clientWidth);
      // extra height = horizontal travel, so scroll maps 1:1
      pin.style.height = `${sticky.offsetHeight + distance}px`;
      readTarget();
      current = target;
      render();
    };

    // scroll the page so the track sits at horizontal offset x
    const scrollToOffset = (x) => {
      const offset = Math.min(distance, Math.max(0, x));
      window.scrollTo({
        top: window.scrollY + pin.getBoundingClientRect().top - pinTop + offset,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    };

    const cardOffset = (card) => card.offsetLeft - cards[0].offsetLeft;

    // dots jump to the scroll position where that card is active
    const dotHandlers = dots.map((dot, i) => {
      const handler = () => scrollToOffset((i / (cards.length - 1)) * distance);
      dot.addEventListener("click", handler);
      return handler;
    });

    const onFocusIn = (e) => {
      const card = e.target.closest(".ms-money-card");
      if (!enabled || !card) return;
      sticky.scrollLeft = 0; // undo any focus scrolling inside the clipped area
      scrollToOffset(cardOffset(card));
    };

    const onKeyDown = (e) => {
      if (!enabled || (e.key !== "ArrowRight" && e.key !== "ArrowLeft")) return;
      if (e.target.closest && e.target.closest("input, textarea, select")) return;
      const rect = pin.getBoundingClientRect();
      const pinned =
        rect.top <= pinTop + 1 && rect.bottom >= pinTop + sticky.offsetHeight - 1;
      const focused = cards.indexOf(document.activeElement);
      if (!pinned && focused < 0) return;
      e.preventDefault();
      const dir = e.key === "ArrowRight" ? 1 : -1;
      if (focused >= 0) {
        const next = cards[focused + dir];
        if (next) next.focus({ preventScroll: true });
        return;
      }
      const step = cards[1] ? cardOffset(cards[1]) : 0;
      scrollToOffset(target + dir * step);
    };

    measure();
    const resizeObserver =
      "ResizeObserver" in window ? new ResizeObserver(measure) : null;
    if (resizeObserver) resizeObserver.observe(sticky);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    window.addEventListener("keydown", onKeyDown);
    track.addEventListener("focusin", onFocusIn);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
      window.removeEventListener("keydown", onKeyDown);
      track.removeEventListener("focusin", onFocusIn);
      dots.forEach((dot, i) => dot.removeEventListener("click", dotHandlers[i]));
      if (frame !== null) cancelAnimationFrame(frame);
      section.classList.remove("is-hscroll");
    };
  }, []);

  // Smooth-scroll every in-page link (nav, buttons, footer)
  const handleAnchorClick = (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    e.preventDefault();
    clearTimeout(hoverTimer.current);
    scrollToSection(link.getAttribute("href").slice(1));
  };

  const handleNavEnter = (id) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => scrollToSection(id), HOVER_DELAY);
  };

  const handleNavLeave = () => clearTimeout(hoverTimer.current);

  return (
    <>
      <PageHelmet pageTitle="Energy Transition" />

      <div className="midstream upstream energy-transition" ref={rootRef} onClick={handleAnchorClick}>
        {/* Nav */}
        <nav className="ms-nav">
          <Container className="ms-nav-inner">
            <Link to="/" className="ms-brand">
              <img src={logoMark} alt="Dsider" />
              <span>Dsider</span>
            </Link>
            <span className="ms-tag">Energy Transition</span>
            <ul className="ms-nav-links">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className={activeId === l.href.slice(1) ? "is-active" : ""}
                    onMouseEnter={() => handleNavEnter(l.href.slice(1))}
                    onMouseLeave={handleNavLeave}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
            <a href="#contact" className="ms-btn ms-btn-sm">
              Find out in two weeks
            </a>
          </Container>
        </nav>

        {/* Hero */}
        <section className="ms-hero">
          <Container>
            <Row className="ms-hero-row align-items-center g-5">
              <Col lg={6} className="ms-hero-copy">
                <span className="ms-badge ms-hero-in" style={d(0)}>
                  For energy transition developers and decarbonizers
                </span>
                <h1 className="ms-title">
                  <span className="ms-hero-in" style={d(1)}>Does the project pay?</span>
                  <em className="ms-hero-in" style={d(3)}>
                    <span>We can show you the</span>
                    <span>math in two weeks.</span>
                  </em>
                </h1>
                <p className="ms-lead ms-hero-in" style={d(4)}>
                  Dsider models your project from feedstock to offtake with the
                  physics built in, and prices every design, incentive, and
                  contract choice in dollars, so the investment case is one you
                  can defend to a lender, a board, or a partner
                </p>
                <div className="ms-actions ms-hero-in" style={d(5)}>
                  <a href="#contact" className="ms-btn">
                    Find out in two weeks &rarr;
                  </a>
                  <a href="#what-we-do" className="ms-btn ms-btn-dark">
                    See what Dsider does &rarr;
                  </a>
                </div>
                <div className="ms-hero-stats">
                  {heroStats.map((s, i) => (
                    <div key={s.label} className="ms-hero-in" style={d(6 + i)}>
                      <span>{s.label}</span>
                    </div>
                  ))}
                </div>
              </Col>
              <Col lg={6} className="ms-hero-media">
                <div className="ms-dash-wrap ms-hero-in ms-from-right" style={d(2)}>
                  <img
                    src={energyHero}
                    alt="Dsider energy transition dashboard"
                    className="ms-dash"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* The problem */}
        <section className="ms-section ms-problem" id="problem">
          <Container>
            <span className="ms-eyebrow ms-reveal">The problem</span>
            <h2 className="ms-heading ms-heading-wide ms-reveal" style={d(1)}>
              <span className="ms-nowrap">Most clean-tech projects are</span>
              <br />
              <em>decided in a spreadsheet.</em>
            </h2>
            <p className="ms-lead ms-problem-lead ms-reveal" style={d(2)}>
              Which pathway. Which capture rate. Which offtake structure.
              Whether the incentive actually qualifies. Most teams answer those
              in a workbook that one person built, with a process model sitting
              in a different file and a financial model in a third. It holds
              until a lender or a partner asks a question the workbook wasn't
              built for.
            </p>
            <Row className="g-4">
              {problems.map(({ icon: Icon, title, text }, i) => (
                <Col md={4} key={title}>
                  <div className="ms-problem-card ms-reveal" style={d(i)}>
                    <span className="ms-icon">
                      <Icon />
                    </span>
                    <h4>{title}</h4>
                    <p>{text}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* What we do */}
        <section className="ms-section ms-what" id="what-we-do">
          <Container>
            <Row className="g-5 align-items-center">
              <Col lg={6}>
                <h2 className="ms-heading ms-reveal" style={d(1)}>
                  From feedstock to offtake,{" "}
                  <em>in one model.</em>
                </h2>
                <p className="ms-lead ms-what-lead ms-reveal" style={d(2)}>
                  Dsider simulates the process, lays your costs, incentives, and
                  offtake terms on top, and prices every choice in dollars and
                  carbon, with the rationale recorded.
                </p>
                <div className="ms-flow ms-reveal" style={d(3)}>
                  {flow.map((f, i) => (
                    <div
                      className={`ms-flow-step ms-${f.tone}`}
                      key={f.label}
                      style={d(i)}
                    >
                      <span className="ms-flow-dot" />
                      <span className="ms-flow-label">{f.label}</span>
                    </div>
                  ))}
                </div>
                <div className="ms-flow-note ms-reveal" style={d(4)}>
                  <span className="ms-badge">
                    Dsider prices every decision along the way
                  </span>
                </div>
              </Col>
              <Col lg={6}>
                <div className="ms-stack">
                  {pillars.map((p, i) => (
                    <div className="ms-pillar ms-reveal" key={p.title} style={d(i + 1)}>
                      <span className="ms-pillar-tag">{p.tag}</span>
                      <h4>{p.title}</h4>
                      <p>{p.text}</p>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Where the money is */}
        <section className="ms-section ms-money" id="money" ref={moneyRef}>
          {/* Pinned horizontal scroll on tablet/desktop; see the effect above */}
          <div className="ms-hscroll-pin" ref={pinRef}>
            <div className="ms-hscroll-sticky">
              <Container>
                <div className="ms-hscroll-head">
                  <div>
                    <span className="ms-eyebrow">Where the Value is hiding</span>
                    <h2 className="ms-heading ms-reveal" style={d(1)}>
                      <span className="ms-nowrap">Where the value is hiding</span>
                    </h2>
                    <p className="ms-lead ms-money-lead ms-reveal" style={d(2)}>
                      One mid-cap operator's own business case put better daily
                      operating and planning decisions at $0.8 to $1.7 million
                      of EBITDA per plant per year.
                    </p>
                  </div>
                </div>
                <div className="ms-reveal" style={d(3)}>
                  <div
                    className="ms-hscroll-track"
                    ref={trackRef}
                    role="region"
                    aria-label="Where the value is hiding"
                  >
                    {moneyPlaces.map((m, i) => (
                      <div
                        className="ms-money-card"
                        key={m.title}
                        tabIndex={0}
                        role="group"
                        aria-label={`${i + 1} of ${moneyPlaces.length}: ${m.title}`}
                      >
                        <div className="ms-money-head">
                          <span className="ms-money-num">0{i + 1}</span>
                          <span className={`ms-money-tag${m.tone ? ` is-${m.tone}` : ""}`}>
                            {m.tag}
                          </span>
                        </div>
                        <h4>{m.title}</h4>
                        <div className="ms-money-shot">
                          <img src={m.image} alt={m.title} />
                        </div>
                        <p>{m.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ms-hscroll-foot">
                  <div className="ms-hscroll-dots" ref={dotsRef}>
                    {moneyPlaces.map((m, i) => (
                      <button
                        type="button"
                        key={m.title}
                        aria-label={`Show card ${i + 1}: ${m.title}`}
                      />
                    ))}
                  </div>
                  <span className="ms-hscroll-hint" ref={hintRef}>
                    Scroll to explore <span aria-hidden="true">&rarr;</span>
                  </span>
                </div>
              </Container>
            </div>
          </div>
          <Container>
            <div className="ms-also ms-reveal">
              <span className="ms-also-label">Also in the model</span>
              {alsoInModel.map((item) => (
                <span className="ms-also-chip" key={item}>
                  {item}
                </span>
              ))}
            </div>
          </Container>
        </section>

        {/* How we start */}
        <section className="ms-section ms-start" id="how">
          <Container>
            <span className="ms-eyebrow ms-reveal">How we start</span>
            <h2 className="ms-heading ms-reveal" style={d(1)}>
              Two weeks to a defined problem.{" "}
              <em>Ninety days to a number.</em>
            </h2>
            <p className="ms-start-lead ms-reveal" style={d(2)}>
              You know what you're buying before you buy it.
            </p>
            <div className="ms-steps">
              {steps.map((s, i) => (
                <div className="ms-step ms-reveal" key={s.title} style={d(i)}>
                  <span className="ms-step-num">0{i + 1}</span>
                  <span className="ms-step-when">{s.when}</span>
                  <h4>{s.title}</h4>
                  <p>{s.text}</p>
                </div>
              ))}
            </div>
            <div className="ms-start-cta ms-reveal">
              <p>No live integration required to start. Our team builds. Yours reviews.</p>
              <a href="#contact" className="ms-btn">
                Find out in two weeks &rarr;
              </a>
            </div>
          </Container>
        </section>

        {/* Who it's built for */}
        <section className="ms-section ms-built">
          <Container>
            <span className="ms-eyebrow ms-reveal">
              Built for the people who have to sign off
            </span>
            <h2 className="ms-heading ms-reveal" style={d(1)}>
              Numbers that hold up
              <br />
              <em>in every room.</em>
            </h2>
            <Row className="g-4">
              {audiences.map((a, i) => (
                <Col md={6} key={a.tag}>
                  <div className="ms-built-card ms-reveal" style={d(i)}>
                    <span className="ms-money-tag">{a.tag}</span>
                    <h3>{a.title}</h3>
                    {a.visual === "seal" ? (
                      <img
                        src={soc2Seal}
                        alt="AICPA SOC 2"
                        className="ms-seal"
                      />
                    ) : (
                      <div className="ms-crop">
                        <img src={inletPriority} alt="Inlet priority simulation" />
                      </div>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Get started */}
        <section className="ms-section ms-cta" id="contact">
          <Container>
            <span className="ms-eyebrow ms-reveal">Get started</span>
            <h2 className="ms-heading ms-reveal" style={d(1)}>
              Want to try it <em>first?</em>
            </h2>
            <p className="ms-lead ms-reveal" style={d(2)}>
              A free, self-serve version of Dsider is available now. Build a
              first case on your own, see how the model prices your project,
              and reach out to us when you want a deeper look.
            </p>
            <div className="ms-reveal" style={d(3)}>
              <MidstreamContactForm />
            </div>
          </Container>
        </section>

        <MidstreamFooter
          segment="Energy Transition"
          tagline="Decision intelligence for energy transition developers. Feedstock to offtake, in one model."
          links={footerLinks}
        />
      </div>
    </>
  );
};

export default EnergyTransition;
